/**
 * PlanningSessionStatusBadge — lifecycle status pill (LD-3).
 * Supports β snake_case + γ TENTATIVE kebab-case aliases (LD-11).
 */

import React from 'react';
import { Loader } from 'lucide-react';

export const STATUS_CONFIG = {
  created: { label: 'Starting…', color: 'blue', spinning: true },
  initializing: { label: 'Starting…', color: 'blue', spinning: true },
  gathering_context: { label: 'Analyzing…', color: 'blue', spinning: true },
  'gathering-context': { label: 'Analyzing…', color: 'blue', spinning: true },
  questions_pending: { label: 'Awaiting input', color: 'orange', spinning: false },
  'awaiting-foreman-input': { label: 'Awaiting input', color: 'orange', spinning: false },
  answers_received: { label: 'Processing…', color: 'orange', spinning: true },
  refining: { label: 'Refining…', color: 'orange', spinning: true },
  tasks_generated: { label: 'Plan ready', color: 'yellow', spinning: false },
  completed: { label: 'Completed', color: 'green', spinning: false },
  approved: { label: 'Approved', color: 'green', spinning: false },
  rejected: { label: 'Rejected', color: 'neutral', spinning: false },
  discarded: { label: 'Discarded', color: 'neutral', spinning: false },
  draft: { label: 'Draft saved', color: 'neutral', spinning: false },
  expired: { label: 'Expired', color: 'neutral', spinning: false },
  failed: { label: 'Plan generation failed', color: 'red', spinning: false },
};

const COLOR_CLASS = {
  blue: 'ps-status-badge--blue',
  orange: 'ps-status-badge--orange',
  yellow: 'ps-status-badge--yellow',
  green: 'ps-status-badge--green',
  neutral: 'ps-status-badge--neutral',
  red: 'ps-status-badge--red',
};

export default function PlanningSessionStatusBadge({ sessionId, status, expiresAt, className = '' }) {
  const config = STATUS_CONFIG[status] || {
    label: status || 'Unknown',
    color: 'neutral',
    spinning: false,
  };

  let expiryLabel = null;
  if (expiresAt && !['completed', 'rejected', 'discarded', 'expired', 'approved'].includes(status)) {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms > 0) {
      const hours = Math.ceil(ms / (1000 * 60 * 60));
      expiryLabel = `Session expires in ${hours}h`;
    } else {
      expiryLabel = 'Session expiring soon';
    }
  }

  return (
    <div className={`ps-status-badge-row ${className}`.trim()} data-session-id={sessionId}>
      <span
        className={`ps-status-badge ${COLOR_CLASS[config.color] || COLOR_CLASS.neutral}`}
        data-testid="planning-session-status-badge"
      >
        {config.spinning ? <Loader size={12} className="ps-status-badge__spin" aria-hidden /> : null}
        {config.label}
      </span>
      {expiryLabel ? (
        <span className="ps-status-badge__expiry" data-testid="planning-session-expiry">
          {expiryLabel}
        </span>
      ) : null}
    </div>
  );
}
